import React from 'react';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import TableDate from '../../TableDate.jsx';

// =============================================================================
// BLOCK TMSPRR.1 — Outstanding Orders TMS Parent Row Component
// Displays parent-level order information with expand/collapse toggle
// =============================================================================
function OutstandingOrdersTMSParentRow({ order, isExpanded, onToggleExpand }) {
  // BLOCK TMSPRR.1.1 — Destructure parent-level order fields
  const {
    SalesOrderNo,
    OrderDate,
    TimeCreated,
    RMANo,
    FacilityName,
    ShipToCode,
    ShipToName,
    CustomerPONo,
    ShipVia,
    ShipStatus,
  } = order;

  const displayFacilityName =
    String(ShipToCode ?? '')
      .trim()
      .toUpperCase() === 'P' && String(FacilityName ?? '').trim()
      ? `${FacilityName} (Ship To Patient)`
      : FacilityName;

  // BLOCK TMSPRR.1.2 — Render parent row with expand/collapse button
  return (
    <TableRow
      hover
      sx={{
        '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
        backgroundColor: (theme) => (isExpanded ? theme.palette.action.hover : 'transparent'),
      }}
      data-cy={`parent-row-${SalesOrderNo}`}
    >
      {/* BLOCK TMSPRR.1.3 — Expand/Collapse Button */}
      <TableCell
        sx={{
          padding: '8px',
          width: '40px',
          textAlign: 'center',
          color: (theme) => theme.palette.text.primary,
        }}
        data-cy="cell-expand"
      >
        <IconButton
          size="small"
          onClick={onToggleExpand}
          data-cy={`expand-btn-${SalesOrderNo}`}
          sx={{ padding: '4px' }}
        >
          {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
        </IconButton>
      </TableCell>

      {/* BLOCK TMSPRR.1.4 — Sales Order Number Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-SalesOrderNo"
      >
        {SalesOrderNo || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.5 — Order Date Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-OrderDate"
      >
        <TableDate date={OrderDate} />
      </TableCell>

      {/* BLOCK TMSPRR.1.5A — Time Created Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-TimeCreated"
      >
        {TimeCreated || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.5B — Created By Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-RMANo"
      >
        {RMANo || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.6 — Facility Name Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-FacilityName"
      >
        {displayFacilityName || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.7 — Ship To Name Column */}
      <TableCell
        sx={{ padding: '8px', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-ShipToName"
      >
        {ShipToName || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.8 — Customer PO Number Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-CustomerPONo"
      >
        {CustomerPONo || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.9 — Ship Via Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-ShipVia"
      >
        {ShipVia || '—'}
      </TableCell>

      {/* BLOCK TMSPRR.1.10 — Ship Status Column */}
      <TableCell
        sx={{ padding: '8px', whiteSpace: 'nowrap', color: (theme) => theme.palette.text.primary }}
        data-cy="cell-ShipStatus"
      >
        {ShipStatus || '—'}
      </TableCell>
    </TableRow>
  );
}

OutstandingOrdersTMSParentRow.propTypes = {
  order: PropTypes.shape({
    SalesOrderNo: PropTypes.string,
    OrderDate: PropTypes.string,
    TimeCreated: PropTypes.string,
    RMANo: PropTypes.string,
    FacilityName: PropTypes.string,
    ShipToCode: PropTypes.string,
    ShipToName: PropTypes.string,
    CustomerPONo: PropTypes.string,
    ShipVia: PropTypes.string,
    ShipStatus: PropTypes.string,
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
};

export default OutstandingOrdersTMSParentRow;
